import React, { useMemo, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, Eye, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatCurrency, formatDate } from "@/lib/formatters";
import { BoletoTransaction, BoletoLine } from "@/lib/api";
import { Button } from "../ui/button";

type CompositionLine = BoletoLine & {
    statusLabel: string;
    parentTransaction: BoletoTransaction;
};

const TYPE_ORDER: Record<string, number> = {
    'Pedido': 1,
    'Return': 2,
    'Discount': 3,
    'Acordo': 4,
    'Expired': 5
};

const isAdjustmentLine = (line: Pick<BoletoLine, "typeLabel">) =>
    line.typeLabel === "Return" || line.typeLabel === "Discount";

interface FinancialCompositionTableProps {
    transactions: BoletoTransaction[];
    isBackoffice: boolean;
    searchQuery?: string;
    onViewBoleto?: (trans: BoletoTransaction) => void;
    currentGeneratingBoletoId?: number | null;
    /** `recIdBoleto`s que a API billet confirmou como indisponíveis — a ação de ver some para eles. */
    unavailableBoletoIds?: ReadonlySet<number>;
    activeFilter?: "all" | "overdue" | "upcoming" | "paid";
}

export const FinancialCompositionTable: React.FC<FinancialCompositionTableProps> = ({
    transactions,
    isBackoffice = false,
    searchQuery = "",
    onViewBoleto,
    currentGeneratingBoletoId = null,
    unavailableBoletoIds,
    activeFilter = "all",
}) => {
    const getTypeLabelText = (type?: string) => {
        switch (type) {
            case "Return":
                return "Devolução";
            case "Discount":
                return "Isenção";
            case "Expired":
                return "Expirado";
            case "Acordo":
                return "Acordo";
            case "Pedido":
            default:
                return "Pedido";
        }
    };

    const getTypeColorClass = (type?: string) => {
        switch (type) {
            case "Return":
                return "text-green-600 group-hover:text-green-700";
            case "Discount":
                return "text-amber-600 group-hover:text-amber-700";
            case "Acordo":
                return "text-blue-600 group-hover:text-blue-700";
            case "Expired":
                return "text-red-600 group-hover:text-red-700";
            default:
                return "text-foreground group-hover:text-primary";
        }
    };

    const getTypeHoverColorClass = (type?: string) => {
        switch (type) {
            case "Return":
                return "group-hover:text-green-700";
            case "Discount":
                return "group-hover:text-amber-700";
            case "Acordo":
                return "group-hover:text-blue-700";
            case "Expired":
                return "group-hover:text-red-700";
            default:
                return "group-hover:text-primary";
        }
    };

    const getTypeBgClass = (type?: string) => {
        switch (type) {
            case "Return":
                return "hover:bg-green-50/60";
            case "Discount":
                return "hover:bg-amber-50/60";
            case "Acordo":
                return "hover:bg-blue-50/60";
            case "Expired":
                return "hover:bg-red-50/60";
            default:
                return "hover:bg-slate-50";
        }
    };

    const processedLines = useMemo(() => {
        if (!transactions || transactions.length === 0) return [];

        const allLines = transactions.flatMap(trans =>
            (trans.lines || []).map(line => ({
                ...line,
                statusLabel: trans.statusLabel,
                parentTransaction: trans
            }))
        );

        let filteredLines = allLines;
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filteredLines = allLines.filter(line =>
                (line.invoiceId && String(line.invoiceId).toLowerCase().includes(query)) ||
                (line.orderId && String(line.orderId).toLowerCase().includes(query)) ||
                (line.salesOrderId && String(line.salesOrderId).toLowerCase().includes(query)) ||
                (line.magentoOrderId && String(line.magentoOrderId).toLowerCase().includes(query)) ||
                (getTypeLabelText(line.typeLabel).toLowerCase().includes(query))
            );
        }

        return filteredLines
            .map(line => {
                let type = line.typeLabel;
                if (!type) {
                    if (line.isDevolucao) type = 'Return';
                    else if (line.isIsencao) type = 'Discount';
                    else if (line.invoiceId?.includes("AC")) type = 'Acordo';
                    else type = 'Pedido';
                }

                const statusLabel = type === 'Return' ? 'Pago' : line.statusLabel;

                return { ...line, typeLabel: type, statusLabel };
            })
            .filter(line => {
                if (activeFilter === "all") return true;
                if (activeFilter === "paid") return line.statusLabel === "Pago";
                if (activeFilter === "overdue") return line.statusLabel === "Vencido";
                if (activeFilter === "upcoming") return line.statusLabel === "Em Aberto";
                return true;
            })
            .sort((a, b) => {
                const orderA = TYPE_ORDER[a.typeLabel as string] || 99;
                const orderB = TYPE_ORDER[b.typeLabel as string] || 99;
                return orderA - orderB;
            });
    }, [transactions, searchQuery, activeFilter]);

    const [openBoletoGroups, setOpenBoletoGroups] = useState<Set<string>>(new Set());

    const boletoGroups = useMemo(() => {
        const groupsByKey = new Map<string, CompositionLine[]>();
        processedLines.forEach((line) => {
            // Devolução/isenção ficam em linha própria: não podem cair no mesmo grupo de um boleto
            // (devoluções e liquidados chegam todos com parmJournalTransRecId = 0).
            const key = isAdjustmentLine(line)
                ? `ajuste-${line.parentTransaction.$id}-${line.$id}`
                : String(line.parentTransaction.parmJournalTransRecId);
            if (!groupsByKey.has(key)) groupsByKey.set(key, []);
            groupsByKey.get(key)!.push(line as CompositionLine);
        });

        return Array.from(groupsByKey.entries()).map(([key, lines]) => {
            const transaction = lines[0].parentTransaction;

            // Devolução/isenção são só informativas: não abatem o valor dos demais itens.
            // Só entram no total quando o grupo tem apenas devolução e/ou isenção.
            const hasOnlyAdjustments = lines.every(isAdjustmentLine);
            const total = lines
                .filter((line) => hasOnlyAdjustments || !isAdjustmentLine(line))
                .reduce((sum, line) => sum + (line.newAmount > 0 ? line.newAmount : line.oldAmount), 0);

            let status = "Em Aberto";
            if (lines.some((line) => line.statusLabel === "Vencido")) status = "Vencido";
            else if (lines.every((line) => line.statusLabel === "Pago")) status = "Pago";

            const allTypeLabels = Array.from(new Set(lines.map((line) => line.typeLabel)))
                .sort((a, b) => (TYPE_ORDER[a as string] || 99) - (TYPE_ORDER[b as string] || 99));
            const commonTypeLabel = allTypeLabels.length === 1 ? allTypeLabels[0] : undefined;

            return {
                key,
                lines,
                transaction,
                total,
                status,
                commonTypeLabel,
                allTypeLabels,
                invoiceCount: new Set(lines.map((line) => line.invoiceId)).size,
            };
        });
    }, [processedLines]);

    const toggleBoletoGroup = (key: string) => {
        setOpenBoletoGroups((prev) => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const renderActionButton = (transaction: BoletoTransaction) => {
        if (!onViewBoleto || !transaction.parmBase64PdfBoleto) return null;
        if (transaction.recIdBoleto && unavailableBoletoIds?.has(transaction.recIdBoleto)) return null;
        const isGenerating = currentGeneratingBoletoId === transaction.parmJournalTransRecId && currentGeneratingBoletoId !== null;
        return (
            <Button
                variant="ghost"
                size="sm"
                className="h-9 w-9 p-0 hover:bg-primary/10 rounded-xl transition-all duration-300 hover:scale-110 active:scale-95 group-hover:shadow-sm"
                onClick={(e) => {
                    e.stopPropagation();
                    onViewBoleto(transaction);
                }}
                disabled={isGenerating}
            >
                {isGenerating ? (
                    <RefreshCw className="h-4 w-4 animate-spin text-primary" />
                ) : (
                    <Eye className="h-4 w-4 text-teal-500 group-hover:text-primary transition-colors duration-300" />
                )}
            </Button>
        );
    };

    const renderLineCells = (line: CompositionLine, boletoPedidoCount?: number) => (
        <>
            <TableCell className="py-5 px-6">
                <div className="flex flex-col gap-0.5">
                    <span className={cn("text-sm font-medium transition-colors duration-300", getTypeColorClass(line.typeLabel))}>
                        {getTypeLabelText(line.typeLabel)}
                    </span>
                    {/* {boletoPedidoCount !== undefined && (
                        <span className="text-[10px] font-medium text-muted-foreground/50">
                            {boletoPedidoCount > 1 ? `${boletoPedidoCount} pedidos neste boleto` : "1 pedido neste boleto"}
                        </span>
                    )} */}
                </div>
            </TableCell>
            <TableCell className="py-5 text-center">
                <span className={cn("text-sm font-medium text-foreground transition-colors duration-300", getTypeHoverColorClass(line.typeLabel))}>
                    {line.typeLabel === 'Return' ? "---" : (line.orderNumber || "---")}
                </span>
            </TableCell>
            {isBackoffice && (
                <>
                    <TableCell className="py-5 text-center">
                        <span className={cn("text-sm font-medium text-muted-foreground/80 transition-colors", getTypeHoverColorClass(line.typeLabel))}>
                            {line.dynamicsSalesId || "---"}
                        </span>
                    </TableCell>
                    <TableCell className="py-5 text-center">
                        <span className={cn("text-sm font-medium text-muted-foreground/80 transition-colors", getTypeHoverColorClass(line.typeLabel))}>
                            {line.magentoOrderId || line.salesOrderId || "---"}
                        </span>
                    </TableCell>
                </>
            )}
            <TableCell className="py-5 text-center">
                <span className={cn("text-sm font-medium text-foreground transition-colors duration-300", getTypeHoverColorClass(line.typeLabel))}>
                    {line.invoiceId || "---"}
                </span>
            </TableCell>
            <TableCell className="py-5 text-center">
                <span className={cn("text-sm font-medium text-muted-foreground/80 transition-colors", getTypeHoverColorClass(line.typeLabel))}>
                    {line.parentTransaction.dueDate ? formatDate(new Date(line.parentTransaction.dueDate)) : "---"}
                </span>
            </TableCell>
            <TableCell className="py-5 text-center">
                <span className="text-sm font-medium text-primary/80 group-hover:text-primary transition-colors">
                    {line.installments || "--"}
                </span>
            </TableCell>
            <TableCell className="py-5 text-right tabular-nums">
                <div className="flex flex-col items-end">
                    <span className={cn(
                        "text-sm font-medium group-hover:scale-105 transition-transform origin-right duration-300",
                        (['Discount', 'Return'].includes(line.typeLabel)) ? 'text-green-800' : 'text-foreground'
                    )}
                    >
                        {formatCurrency(line.newAmount > 0 ? line.newAmount : line.oldAmount)}
                    </span>
                    {line.newAmount > 0 && line.newAmount !== line.oldAmount && (
                        <span className="text-[10px] text-red-500/70 line-through font-bold">
                            {formatCurrency(line.oldAmount)}
                        </span>
                    )}
                </div>
            </TableCell>
            <TableCell className="py-5 text-center">
                <Badge className={cn(
                    "rounded-full px-4 py-1 text-[9px] font-bold uppercase border-none shadow-sm transition-all duration-300 group-hover:shadow-md group-hover:-translate-y-0.5",
                    line.statusLabel === "Pago" ? "bg-green-100 text-green-700 hover:bg-green-200" :
                        line.statusLabel === "Vencido" ? "bg-red-100 text-red-600 hover:bg-red-200" :
                            "bg-blue-100 text-blue-600 hover:bg-blue-200"
                )}>
                    {line.statusLabel.toUpperCase()}
                </Badge>
            </TableCell>
        </>
    );

    if (processedLines.length === 0) {
        return (
            <div className="py-8 text-center text-muted-foreground text-xs font-medium italic border rounded-xl bg-white shadow-sm">
                Sem composição detalhada disponível.
            </div>
        );
    }

    return (
        <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
            <Table>
                <TableHeader className="bg-slate-100/80 border-b-2 border-slate-200">
                    <TableRow className="hover:bg-transparent border-none">
                        <TableHead className="h-11 text-[10px] font-extrabold uppercase tracking-widest text-black px-6">Tipo</TableHead>
                        <TableHead className="h-11 text-[10px] font-extrabold uppercase tracking-widest text-black text-center">Nº Pedido</TableHead>
                        {isBackoffice && (
                            <>
                                <TableHead className="h-11 text-[10px] font-extrabold uppercase tracking-widest text-black text-center">OV</TableHead>
                                <TableHead className="h-11 text-[10px] font-extrabold uppercase tracking-widest text-black text-center">Pedido Magento</TableHead>
                            </>
                        )}
                        <TableHead className="h-11 text-[10px] font-extrabold uppercase tracking-widest text-black text-center">Nota Fiscal</TableHead>
                        <TableHead className="h-11 text-[10px] font-extrabold uppercase tracking-widest text-black text-center">Vencimento</TableHead>
                        <TableHead className="h-11 text-[10px] font-extrabold uppercase tracking-widest text-black text-center">Parcela</TableHead>
                        <TableHead className="h-11 text-[10px] font-extrabold uppercase tracking-widest text-black text-right">Valor</TableHead>
                        <TableHead className="h-11 text-[10px] font-extrabold uppercase tracking-widest text-black text-center">Status</TableHead>
                        <TableHead className="h-11 text-[10px] font-extrabold uppercase tracking-widest text-black text-center px-6">Ação</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {boletoGroups.map((group) => {
                        if (group.lines.length === 1) {
                            const line = group.lines[0];
                            return (
                                <TableRow
                                    key={group.key}
                                    className={cn(
                                        "transition-all duration-300 border-b border-slate-100 last:border-0 group",
                                        getTypeBgClass(line.typeLabel)
                                    )}
                                >
                                    {renderLineCells(line, 1)}
                                    <TableCell className="py-5 text-center px-6">
                                        {line.typeLabel === 'Return' ? (
                                            <span className="text-sm font-medium text-muted-foreground/40">---</span>
                                        ) : (
                                            renderActionButton(line.parentTransaction)
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        }

                        const isOpen = openBoletoGroups.has(group.key);

                        return (
                            <React.Fragment key={group.key}>
                                <TableRow
                                    onClick={() => toggleBoletoGroup(group.key)}
                                    className="cursor-pointer transition-all duration-300 border-b border-slate-100 last:border-0 hover:bg-slate-50"
                                >
                                    <TableCell className="py-5 px-6">
                                        <div className="flex items-center gap-3">
                                            <div className={cn(
                                                "p-1.5 rounded-lg transition-transform bg-slate-50 text-primary",
                                                isOpen && "rotate-180"
                                            )}>
                                                <ChevronDown className="h-3.5 w-3.5" />
                                            </div>
                                            <div className="flex flex-col gap-0.5">
                                                {group.allTypeLabels.length > 1 ? (
                                                    <span className="text-sm font-medium">
                                                        {group.allTypeLabels.map((type, index) => (
                                                            <React.Fragment key={type}>
                                                                <span className={cn("transition-colors duration-300", getTypeColorClass(type))}>
                                                                    {getTypeLabelText(type)}
                                                                </span>
                                                                {index < group.allTypeLabels.length - 1 && (
                                                                    <span className="text-muted-foreground/50">{" + "}</span>
                                                                )}
                                                            </React.Fragment>
                                                        ))}
                                                    </span>
                                                ) : (
                                                    <span className={cn("text-sm font-medium transition-colors duration-300", getTypeColorClass(group.commonTypeLabel))}>
                                                        {getTypeLabelText(group.commonTypeLabel)}
                                                    </span>
                                                )}
                                                {/* <span className="text-[10px] font-medium text-muted-foreground/50">
                                                    {group.lines.length} pedidos neste boleto
                                                </span> */}
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell className="py-5 text-center">
                                        {/* <span className="text-sm font-medium text-foreground">{group.lines.length} pedidos</span> */}
                                    </TableCell>
                                    {isBackoffice && (
                                        <>
                                            <TableCell className="py-5 text-center">
                                                <span className="text-sm font-medium text-muted-foreground/60">---</span>
                                            </TableCell>
                                            <TableCell className="py-5 text-center">
                                                <span className="text-sm font-medium text-muted-foreground/60">---</span>
                                            </TableCell>
                                        </>
                                    )}
                                    <TableCell className="py-5 text-center">
                                        {/* <span className="text-sm font-medium text-foreground">{group.invoiceCount} notas</span> */}
                                    </TableCell>
                                    <TableCell className="py-5 text-center">
                                        <span className="text-sm font-medium text-muted-foreground/80">
                                            {group.transaction.dueDate ? formatDate(new Date(group.transaction.dueDate)) : "---"}
                                        </span>
                                    </TableCell>
                                    <TableCell className="py-5 text-center">
                                        {/* <span className="text-sm font-medium text-muted-foreground/60">Múltiplas</span> */}
                                    </TableCell>
                                    <TableCell className="py-5 text-right tabular-nums">
                                        <span className="text-sm font-bold text-foreground">{formatCurrency(group.total)}</span>
                                    </TableCell>
                                    <TableCell className="py-5 text-center">
                                        <Badge className={cn(
                                            "rounded-full px-4 py-1 text-[9px] font-bold uppercase border-none shadow-sm",
                                            group.status === "Pago" ? "bg-green-100 text-green-700" :
                                                group.status === "Vencido" ? "bg-red-100 text-red-600" :
                                                    "bg-blue-100 text-blue-600"
                                        )}>
                                            {group.status.toUpperCase()}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="py-5 text-center px-6">
                                        {renderActionButton(group.transaction)}
                                    </TableCell>
                                </TableRow>

                                {isOpen && group.lines.map((line, index) => (
                                    <TableRow
                                        key={`${group.key}-${line.$id}-${index}`}
                                        className={cn(
                                            "transition-all duration-300 border-b border-slate-100 last:border-0 bg-slate-100 border-l-2 border-l-primary/30",
                                            getTypeBgClass(line.typeLabel)
                                        )}
                                    >
                                        {renderLineCells(line)}
                                        <TableCell className="py-5 text-center px-6">
                                            <span className="text-sm font-medium text-muted-foreground/40">---</span>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </React.Fragment>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
};
